import { useState, type CSSProperties } from "react";
import type { ServiceGroupView } from "@majourneys/shared";

/**
 * The chosen card's icon pops in, then idles: the dinner cloche floats, the
 * bottle tray sways from its base. The other card stays still, so the motion
 * itself says which kind of night is picked. Inline, not in an app stylesheet,
 * because the package also renders on the city sites. Reduced motion: still.
 */
const ICON_MOTION_CSS = `
@keyframes mjSvcHop {
  0% { transform: translateY(0) scale(1, 1); }
  18% { transform: translateY(0) scale(1.12, 0.86); }
  48% { transform: translateY(-12px) scale(0.94, 1.08); }
  72% { transform: translateY(0) scale(1.08, 0.92); }
  86% { transform: translateY(-2px) scale(0.98, 1.02); }
  100% { transform: translateY(0) scale(1, 1); }
}
@keyframes mjSvcClink {
  0% { transform: translateY(0) rotate(0deg) scale(1); }
  15% { transform: translateY(-6px) rotate(-12deg) scale(1.1); }
  35% { transform: translateY(-6px) rotate(10deg) scale(1.1); }
  55% { transform: translateY(-3px) rotate(-7deg) scale(1.05); }
  75% { transform: translateY(0) rotate(4deg) scale(1); }
  100% { transform: translateY(0) rotate(0deg) scale(1); }
}
@keyframes mjSvcFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
@keyframes mjSvcSway { 0%, 100% { transform: rotate(0deg); } 25% { transform: rotate(-4deg); } 75% { transform: rotate(4deg); } }
.mj-svc-float { animation: mjSvcHop 560ms cubic-bezier(0.33, 1, 0.68, 1), mjSvcFloat 2600ms ease-in-out 560ms infinite; transform-origin: 50% 100%; }
.mj-svc-sway { animation: mjSvcClink 640ms cubic-bezier(0.33, 1, 0.68, 1), mjSvcSway 2800ms ease-in-out 640ms infinite; transform-origin: 50% 88%; }
.mj-svc-card { transition: transform 120ms ease-out, background-color 200ms, box-shadow 200ms; }
.mj-svc-card:active { transform: scale(0.97); }
@media (prefers-reduced-motion: reduce) { .mj-svc-float, .mj-svc-sway { animation: none; } .mj-svc-card:active { transform: none; } }
`;

/** How one chip or tab looks: the sheet and the sidebar each pass their own. */
export type ChipLook = (active: boolean) => { className: string; style?: CSSProperties };

/**
 * "Kind of night first" time picker for a venue with service groups
 * (L'Épicurien: Dinner, or Bottle service lounge). The guest picks the kind
 * of night, then a time under that group's service headings ("1st service").
 * A time picked here always carries its service, since two services can run
 * at the same minute.
 *
 * The tab shown is the selected service's group; tapping another tab picks
 * that group's first time, so what is selected is always on screen.
 */
export function ServiceGroupPicker({
  groups,
  time,
  serviceId,
  onPick,
  onSwitchGroup = onPick,
  chip,
  iconSrc,
  groupLabel,
}: {
  readonly groups: ServiceGroupView[];
  readonly time: string | null;
  readonly serviceId: string | null;
  readonly onPick: (time: string, serviceId: string) => void;
  /** Switching tab picks that group's first time; defaults to `onPick`. */
  readonly onSwitchGroup?: (time: string, serviceId: string) => void;
  readonly chip: ChipLook;
  /** The host's file for a group's icon key; none passed, no icon drawn. */
  readonly iconSrc?: (key: string) => string | null;
  /** Screen-reader name of the tab row ("Your night"). */
  readonly groupLabel: string;
}) {
  const activeGroup =
    groups.find((g) => g.sections.some((s) => s.service.id === serviceId && time && s.times.includes(time))) ??
    groups.find((g) => g.sections.some((s) => time && s.times.includes(time))) ??
    groups[0];
  // Every tap replays the icon's jump, even on the card already chosen: the
  // count is in the icon's key, so a new tap mounts it fresh.
  const [taps, setTaps] = useState<Record<string, number>>({});
  if (!activeGroup) return null;
  return (
    <div>
      <style>{ICON_MOTION_CSS}</style>
      {groups.length > 1 && (
        /* Two cards, icon over name over hours (owner picked "B", 2026-11-05):
           the kind of night must never read as one more navy time chip. */
        <div role="group" aria-label={groupLabel} className="grid grid-cols-2 gap-2">
          {groups.map((g) => {
            const on = g.id === activeGroup.id;
            const src = g.icon ? (iconSrc?.(g.icon) ?? null) : null;
            return (
              <button
                key={g.id}
                type="button"
                aria-pressed={on}
                onClick={() => {
                  setTaps((t) => ({ ...t, [g.id]: (t[g.id] ?? 0) + 1 }));
                  if (on) return;
                  const first = g.sections[0];
                  if (first?.times[0]) onSwitchGroup(first.times[0], first.service.id);
                }}
                className="mj-svc-card flex min-h-[44px] flex-col items-center justify-start rounded-2xl px-2 pb-2.5 pt-2 text-center font-sans transition-colors"
                style={{
                  background: on ? "#f3f2fb" : "#ffffff",
                  boxShadow: on ? "inset 0 0 0 1.5px #1b1754" : "inset 0 0 0 1px rgba(17,14,57,0.12)",
                }}
              >
                {src && (
                  <img
                    key={on ? `on-${taps[g.id] ?? 0}` : "off"}
                    src={src}
                    alt=""
                    aria-hidden
                    className={`h-12 w-12 object-contain ${on ? (g.icon === "bottle-service" ? "mj-svc-sway" : "mj-svc-float") : ""}`}
                    draggable={false}
                    // A file that does not load leaves no broken-image box.
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                )}
                <span className="mt-1 text-[12.5px] font-bold leading-tight text-[#110E39]">{g.label}</span>
                <span dir="ltr" className="mt-0.5 text-[11px] font-semibold text-[#5a5d77]">{g.hint}</span>
              </button>
            );
          })}
        </div>
      )}
      {activeGroup.sections.map((sec) => {
        const cols = sec.heading ? "grid-cols-3" : "grid-cols-4";
        return (
          <div key={sec.service.id} className="mt-3">
            {sec.heading && (
              <p className="mb-1.5 font-sans text-[11.5px] font-semibold text-[#5a5d77]">{sec.heading}</p>
            )}
            <div className={`grid ${cols} gap-1.5`}>
              {sec.times.map((t) => {
                const on = t === time && sec.service.id === serviceId;
                const look = chip(on);
                return (
                  <button
                    key={t}
                    type="button"
                    aria-pressed={on}
                    aria-label={`${t} · ${sec.heading ? `${activeGroup.label} ${sec.heading}` : activeGroup.label}`}
                    onClick={() => onPick(t, sec.service.id)}
                    className={`min-h-[34px] px-0 py-1.5 font-sans text-[12px] font-bold transition ${look.className}`}
                    style={look.style}
                  >
                    <span dir="ltr">{t}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
